import React, { useEffect, useRef, useState } from 'react';
import { useApi } from '../api/client';
import { googleTicketKey, googleStateKey, googleResultKey } from '../utils/googleConnectionNavigation';
import '../styles/connections.css';
import './GoogleConnectionPage.css';
import GoogleRegistrationSettings, { type GoogleRegistration } from '../components/GoogleRegistrationSettings';
import { Link } from 'react-router-dom';

type Account = { id: string; name: string; connected: boolean };
type Status = { configured: boolean; callback_url: string; can_configure: boolean; registration?: GoogleRegistration | null; accounts: Account[]; grants?: { id: string; app_name: string; consumer_origin: string }[] };
type Preview = { app_name: string; consumer_origin: string };

export default function GoogleConnectionPage({ apiClient }: { apiClient: ReturnType<typeof useApi> }) {
  const [ticket] = useState(() => sessionStorage.getItem(googleTicketKey));
  const [status, setStatus] = useState<Status | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [selected, setSelected] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const completion = useRef<Promise<unknown> | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const raw = sessionStorage.getItem(googleResultKey);
      if (raw) {
        sessionStorage.removeItem(googleResultKey);
        try {
          const result = JSON.parse(raw) as { error?: string; state?: string; code?: string };
          if (result.error) { if (active) setError(result.error); }
          else completion.current = apiClient.post('/api/google-owner/oauth/complete', { state: result.state, code: result.code });
        } catch { if (active) setError('Google connection did not complete. Please connect again.'); }
      }
      if (completion.current) {
        try { await completion.current; }
        catch { if (active) setError('Google connection did not complete. Please connect again.'); }
      }
      const current = await apiClient.get<Status>('/api/google-owner/status');
      const request = ticket ? await apiClient.post<Preview>('/api/google-owner/handoff/preview', { ticket }) : null;
      if (active) { setStatus(current); setPreview(request); }
    };
    void load().catch(() => { if (active) setError('Could not load this connection. Sign in again or start a new connection from your app.'); });
    return () => { active = false; };
  }, [apiClient, ticket, reload]);

  async function perform(action: () => Promise<void>) {
    if (busy) return;
    setBusy(true); setError('');
    try { await action(); } catch { setError('The connection could not be completed. Please try again.'); }
    finally { setBusy(false); }
  }

  async function connect() {
    await perform(async () => {
      const result = await apiClient.post<{ authorization_url: string }>('/api/google-owner/oauth/start', { consent: true });
      const url = new URL(result.authorization_url);
      const state = url.searchParams.get('state');
      if (url.origin !== 'https://accounts.google.com' || url.pathname !== '/o/oauth2/v2/auth' || !state || !/^[A-Za-z0-9_-]{43}$/.test(state)) throw new Error('Invalid authorization response');
      sessionStorage.setItem(googleStateKey, state);
      window.location.assign(url.toString());
    });
  }

  async function approve() {
    if (!ticket || !preview || !selected || !consent) return;
    await perform(async () => {
      const result = await apiClient.post<{ redirect_url: string }>('/api/google-owner/handoff/approve', { ticket, account_id: selected, consent });
      const url = new URL(result.redirect_url);
      if (url.origin !== preview.consumer_origin || url.pathname !== '/auth/google/callback') throw new Error('Invalid return address');
      sessionStorage.removeItem(googleTicketKey);
      window.location.assign(url.toString());
    });
  }

  return <div className="google-connections">
    <header className="connection-intro">
      {status?.can_configure && <Link className="connection-back" to="/settings/connections"><span aria-hidden="true">←</span> Connections &amp; secrets</Link>}
      <h2>{preview ? `Connect Google Drive to ${preview.app_name}` : 'Google Drive connections'}</h2>
      <p>Connect your Google account once. Choose which apps can use their own private folder.</p>
    </header>
    {error && <p className="connection-notice connection-notice--error" role="alert">{error}</p>}
    {!status && !error && <p role="status">Loading connections…</p>}
    {status && <>
      {!status.configured && <p className="connection-notice">The shared Google setup needs to be completed once by the Mission Control administrator.</p>}
      {preview && <section className="card google-approval">
        <div className="connection-section-heading"><div><h3>App access request</h3><p><strong>{preview.app_name}</strong> at {preview.consumer_origin} wants to create and use its own private Google Drive folder for PDFs.</p></div></div>
        {status.configured && !status.accounts.some(account => account.connected) && <button disabled={busy} onClick={() => { void connect(); }}>Connect a Google account</button>}
        <label>Google account <select value={selected} onChange={event => { setSelected(event.target.value); setConsent(false); }} disabled={busy}>
          <option value="">Choose an account</option>
          {status.accounts.filter(account => account.connected).map(account => <option key={account.id} value={account.id}>{account.name}</option>)}
        </select></label>
        <label className="google-consent"><input type="checkbox" checked={consent} onChange={event => setConsent(event.target.checked)} disabled={busy || !selected} /><span>Allow this app to create its private folder and save PDFs there.</span></label>
        <div className="google-actions">
          <button disabled={busy || !selected || !consent} onClick={() => { void approve(); }}>Allow and return to {preview.app_name}</button>
          {status.configured && status.accounts.some(account => account.connected) && <button className="secondary" disabled={busy} onClick={() => { void connect(); }}>Use another Google account</button>}
        </div>
      </section>}
      {!preview && status.configured && <section className="card google-account-panel">
        <div className="connection-section-heading"><div><h3>Google accounts</h3><p>Manage your connected accounts and their access across apps.</p></div>
          <button disabled={busy} onClick={() => { void connect(); }}>Connect a Google account</button>
        </div>
        {status.accounts.length === 0 && <p className="connection-empty">No Google accounts connected yet.</p>}
        {status.accounts.map(account => <div className="google-connection-row" key={account.id}>
          <div className="google-connection-name"><strong>{account.name}</strong><span className={`connection-status${account.connected ? ' connection-status--success' : ''}`}>{account.connected ? 'Connected' : 'Disconnected'}</span></div>
          {account.connected && <button className="secondary" disabled={busy} onClick={() => {
            if (!window.confirm('Disconnect this Google account from all apps? Saved files will stay in Google Drive.')) return;
            void perform(async () => { await apiClient.post(`/api/google-owner/accounts/${encodeURIComponent(account.id)}/disconnect`, { consent: true }); setReload(value => value + 1); });
          }}>Disconnect from all apps</button>}
        </div>)}
      </section>}
      {!preview && Boolean(status.grants?.length) && <section className="card google-account-panel">
        <div className="connection-section-heading"><div><h3>Connected apps</h3><p>Revoke one app’s access while keeping your other connections.</p></div></div>
        {status.grants?.map(grant => <div className="google-connection-row" key={grant.id}>
        <div className="google-connection-name"><strong>{grant.app_name}</strong><p className="connection-help">{grant.consumer_origin}</p></div>
        <button className="secondary" disabled={busy} onClick={() => { void perform(async () => {
          await apiClient.post(`/api/google-owner/grants/${encodeURIComponent(grant.id)}/disconnect`, { consent: true });
          setReload(value => value + 1);
        }); }}>Disconnect {grant.app_name}</button>
      </div>)}
      </section>}
      {status.can_configure && (!preview || !status.configured) && <GoogleRegistrationSettings key={`${reload}:${status.registration?.revision ?? 'new'}`} apiClient={apiClient} callbackUrl={status.callback_url} registration={status.registration} configured={status.configured} onUpdated={() => { setStatus(null); setReload(value => value + 1); }} />}
    </>}
  </div>;
}
